import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { setBranchPreviewChatSession } from 'shared/api/branchPreviewSession';

import { useSandboxes } from '../views/hooks/useOntologyViews';
import { readSandboxPreviewFromSearch, stripSandboxPreviewFromSearch } from './utils/sandboxPreviewFromUrl';
import { parseDraftReviewRoute } from './utils/previewDestination';
import { buildSandboxContext, mergeSandboxContextHints } from './utils/sandboxContext';

const SandboxSessionContext = createContext(null);

/**
 * Global draft preview state. Sandboxes are selected via an agent and apply as
 * an overlay lens across Apps views and Configure editors — not a separate
 * navigation destination.
 */
const readOverlayHintFields = (hints) => {
  if (!hints || typeof hints !== 'object') return null;
  return {
    approval_id: hints.approval_id || null,
    active_overlay_id: hints.active_overlay_id || null,
    workspace_snapshot_key: hints.workspace_snapshot_key || null,
    baseline_workspace_snapshot_key: hints.baseline_workspace_snapshot_key || null,
    parent_environment_snapshot_id: hints.parent_environment_snapshot_id || null,
    source_session_id: hints.source_session_id || null,
    sandbox_name: hints.sandbox_name || null,
  };
};

const readE2eSandboxPreviewSeed = () => {
  if (typeof window === 'undefined') return null;
  const previewSeed = window.__E2E_SANDBOX_PREVIEW__;
  if (!previewSeed || typeof previewSeed !== 'object') return null;
  const sandboxId = previewSeed.sandbox_id || previewSeed.sandboxId || null;
  if (!sandboxId) return null;
  return {
    sandbox_id: sandboxId,
    sandbox_name: previewSeed.sandbox_name || previewSeed.sandboxName || null,
    active_overlay_id: previewSeed.active_overlay_id || previewSeed.activeOverlayId || null,
    workspace_snapshot_key:
      previewSeed.workspace_snapshot_key || previewSeed.workspaceSnapshotKey || null,
    baseline_workspace_snapshot_key:
      previewSeed.baseline_workspace_snapshot_key ||
      previewSeed.baselineWorkspaceSnapshotKey ||
      null,
    parent_environment_snapshot_id:
      previewSeed.parent_environment_snapshot_id ||
      previewSeed.parentEnvironmentSnapshotId ||
      null,
    source_session_id: previewSeed.source_session_id || previewSeed.sourceSessionId || null,
  };
};

export const SandboxSessionProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [requestedSandboxId, setPreviewSandboxId] = useState(null);
  const [activeChatSessionId, setChatSessionId] = useState(null);
  const setActiveChatSessionId = useCallback((sessionId) => {
    setBranchPreviewChatSession(sessionId);
    setChatSessionId(sessionId || null);
  }, []);
  const [requestedPreviewHints, setPreviewSandboxHints] = useState(null);
  const [isRevisionPinned, setIsRevisionPinned] = useState(false);
  const [showChangesPanel, setShowChangesPanel] = useState(false);
  const [showPublicationPanel, setShowPublicationPanel] = useState(false);
  const [isDraftPickerOpen, setDraftPickerOpen] = useState(false);
  const appliedUrlRef = useRef('');
  const {
    data: availableSandboxes = [],
    isSuccess: sandboxListSucceeded,
    dataUpdatedAt: sandboxesUpdatedAt,
    isFetching: isFetchingSandboxes,
    error: sandboxesError,
    refetch: refetchSandboxes,
  } = useSandboxes({
    enabled: true,
    chatSessionId: activeChatSessionId,
    watching: Boolean(requestedSandboxId || isDraftPickerOpen || parseDraftReviewRoute(location.pathname).sandboxId),
  });
  // Only a fetched list says which previews this person may open. Until sign-in
  // resolves the list is disabled, so it reports neither loading nor fetching;
  // treating that as an empty list would drop a reloaded review URL. Placeholder
  // data was never fetched for this list, so it has no update time.
  const hasSandboxList = Boolean(sandboxListSucceeded && sandboxesUpdatedAt > 0);
  const isLoadingSandboxes = !hasSandboxList && !sandboxesError;

  // Never reuse chat A's cached working preview during the transition to chat B.
  const sandboxes = useMemo(() => sandboxesError ? [] : availableSandboxes.filter((row) =>
    row.preview_access === 'approval' || (row.preview_access === 'chat'
      && activeChatSessionId && row.source_session_id === activeChatSessionId)
  ), [availableSandboxes, activeChatSessionId, sandboxesError]);

  const selectedSandbox = useMemo(
    () => sandboxes.find((sandbox) => sandbox.sandbox_id === requestedSandboxId
      && ((sandbox.preview_access === 'chat' && !isRevisionPinned)
          || (sandbox.working_review_id && (sandbox.working_review_id === requestedPreviewHints?.approval_id
              || sandbox.head_revision_id === requestedPreviewHints?.active_overlay_id))
          || !requestedPreviewHints?.active_overlay_id || requestedPreviewHints.active_overlay_id === sandbox.active_overlay_id)
      && (!requestedPreviewHints?.approval_id || (sandbox.preview_approval_ids || [sandbox.publication?.approval_id]).includes(requestedPreviewHints.approval_id))) || null,
    [sandboxes, requestedSandboxId, requestedPreviewHints, isRevisionPinned]
  );
  const previewSandboxId = selectedSandbox?.sandbox_id || null;
  const refreshWorkingPreview = selectedSandbox && requestedPreviewHints?.active_overlay_id !== selectedSandbox.active_overlay_id
    && (selectedSandbox.preview_access === 'chat' || selectedSandbox.working_review_id);
  const previewSandboxHints = refreshWorkingPreview ? null : requestedPreviewHints;

  // Keep the decision identity when its own Action advances the working snapshot.
  useEffect(() => {
    if (selectedSandbox?.preview_access !== 'approval') return;
    if (previewSandboxHints?.active_overlay_id
        && (previewSandboxHints?.approval_id || !selectedSandbox.publication?.approval_id)) return;
    setPreviewSandboxHints((previous) => ({ ...previous,
      active_overlay_id: selectedSandbox.active_overlay_id,
      approval_id: previous?.approval_id || selectedSandbox.publication?.approval_id || null,
    }));
  }, [selectedSandbox, previewSandboxHints?.active_overlay_id, previewSandboxHints?.approval_id]);

  const sandboxContext = useMemo(() => {
    const baseContext = buildSandboxContext(selectedSandbox, {
      // Branch previews always evaluate proposed changes against workspace records.
      instancePreviewMode: 'include_live',
    });
    if (!baseContext) return null;
    return mergeSandboxContextHints(baseContext, previewSandboxHints);
  }, [selectedSandbox, previewSandboxId, previewSandboxHints]);

  const isPreviewActive = Boolean(previewSandboxId);

  const applyPreviewSelection = useCallback((sandboxId, hints = null) => {
    setIsRevisionPinned(false);
    setPreviewSandboxId(sandboxId || null);
    const hintSandboxId = hints?.sandbox_id || sandboxId || null;
    setPreviewSandboxHints(
      sandboxId && hintSandboxId === sandboxId ? readOverlayHintFields(hints) : null
    );
    setShowChangesPanel(Boolean(hints?.show_changes));
    setShowPublicationPanel(false);
  }, []);

  // Saved review URLs resolve before arriving here; native links activate the overlay.
  useEffect(() => {
    const signature = `${location.pathname}${location.search}`;
    if (appliedUrlRef.current === signature) return;
    appliedUrlRef.current = signature;
    if (parseDraftReviewRoute(location.pathname).sandboxId) return;
    const preview = readSandboxPreviewFromSearch(location.search);
    if (preview) {
      setIsRevisionPinned(Boolean(preview.active_overlay_id || preview.approval_id));
      setPreviewSandboxId(preview.sandbox_id);
      // A child can hydrate the reviewed snapshot in this same effect flush.
      // Merge into the latest state, not the render-time hints, or URL adoption
      // can erase that hydration and leave a cached revision loading forever.
      setPreviewSandboxHints((previous) => {
        const sameRevision = preview.sandbox_id === previewSandboxId &&
          (!preview.active_overlay_id || preview.active_overlay_id === previous?.active_overlay_id);
        return readOverlayHintFields(sameRevision ? { ...previous, ...preview } : preview);
      });
      setShowChangesPanel(Boolean(preview.show_changes));
      setShowPublicationPanel(false);
    }
    else setShowChangesPanel(false);
  }, [location.pathname, location.search, applyPreviewSelection, previewSandboxId, previewSandboxHints]);

  const selectPreviewSandbox = useCallback((sandboxId, hints = null) => {
    applyPreviewSelection(sandboxId, hints);
    const params = new URLSearchParams(location.search);
    if (parseDraftReviewRoute(location.pathname).sandboxId) return;
    if (!['sandbox_id', 'sandbox', 'overlay'].some((key) => params.has(key))) return;
    const search = stripSandboxPreviewFromSearch(params);
    navigate({ pathname: location.pathname, search: search ? `?${search}` : '', hash: location.hash }, { replace: true });
  }, [applyPreviewSelection, location.pathname, location.search, location.hash, navigate]);

  const clearPreview = useCallback(() => {
    setIsRevisionPinned(false);
    setPreviewSandboxId(null);
    setPreviewSandboxHints(null);
    setShowChangesPanel(false);
    setShowPublicationPanel(false);
    const params = new URLSearchParams(location.search);
    if (parseDraftReviewRoute(location.pathname).sandboxId) return;
    if (!['sandbox_id', 'sandbox', 'overlay', 'artifact'].some((key) => params.has(key))) return;
    const nextSearch = stripSandboxPreviewFromSearch(params);
    navigate(
      { pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '', hash: location.hash },
      { replace: true }
    );
  }, [location.hash, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (requestedSandboxId && !selectedSandbox && hasSandboxList
        && !isFetchingSandboxes && !sandboxesError) clearPreview();
  }, [requestedSandboxId, selectedSandbox, hasSandboxList, isFetchingSandboxes, sandboxesError, clearPreview]);

  useEffect(() => () => setBranchPreviewChatSession(null), []);

  /** Drop checkpoint hints so the next sandbox-list refetch drives overlay resolution. */
  const clearPreviewHints = useCallback(() => {
    if (selectedSandbox?.preview_access === 'chat') setPreviewSandboxHints(null);
    // Keep the review id while the refreshed list resolves its new working snapshot.
  }, [selectedSandbox]);

  // Keep an inspected revision pinned even when the branch list catches up or advances.
  const pinPreviewRevision = useCallback((sandboxId, hints) => {
    if (sandboxId !== previewSandboxId) return;
    if (hints?.active_overlay_id && hints.active_overlay_id !== selectedSandbox?.active_overlay_id) return;
    setPreviewSandboxHints((previous) => ({ ...previous, ...readOverlayHintFields(hints) }));
  }, [previewSandboxId, selectedSandbox]);
  const toggleChangesPanel = useCallback(() => setShowChangesPanel((shown) => !shown), []);
  const togglePublicationPanel = useCallback(() => setShowPublicationPanel((shown) => !shown), []);


  const sandboxSession = useMemo(() => {
    if (!sandboxContext) return null;
    return {
      sandboxId: sandboxContext.sandbox_id,
      sandboxName: selectedSandbox?.sandbox_name || null,
      sandboxContext,
    };
  }, [sandboxContext, selectedSandbox?.sandbox_name]);

  const setSandboxSession = useCallback(
    (next) => {
      const nextContext = next?.sandboxContext || null;
      const nextId = next?.sandboxId || nextContext?.sandbox_id || null;
      if (nextId) {
        selectPreviewSandbox(nextId, nextContext?.sandbox_id === nextId ? nextContext : null);
      } else {
        clearPreview();
      }
    },
    [selectPreviewSandbox, clearPreview]
  );

  useEffect(() => {
    const previewSeed = readE2eSandboxPreviewSeed();
    if (!previewSeed) return undefined;
    applyPreviewSelection(previewSeed.sandbox_id, previewSeed);
    return undefined;
  }, [applyPreviewSelection]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    window.__E2E_SANDBOX_SESSION__ = {
      activate: (next) => setSandboxSession(next),
      clear: () => clearPreview(),
      getContext: () => sandboxContext,
      isPreviewActive,
    };
    return () => {
      delete window.__E2E_SANDBOX_SESSION__;
    };
  }, [setSandboxSession, clearPreview, sandboxContext, isPreviewActive]);

  const value = useMemo(
    () => ({
      sandboxSession,
      activeChatSessionId,
      setActiveChatSessionId,
      setSandboxSession,
      clearSandboxSession: clearPreview,
      previewSandboxId,
      previewSandboxHints,
      selectedSandbox,
      sandboxContext,
      isPreviewActive,
      isLoadingSandboxes,
      isFetchingSandboxes,
      sandboxesError,
      refetchSandboxes,
      sandboxes,
      selectPreviewSandbox,
      clearPreview,
      clearPreviewHints,
      pinPreviewRevision,
      showChangesPanel,
      toggleChangesPanel,
      showPublicationPanel,
      togglePublicationPanel,
      setDraftPickerOpen,
    }),
    [
      sandboxSession,
      activeChatSessionId,
      setActiveChatSessionId,
      setSandboxSession,
      clearPreview,
      clearPreviewHints,
      pinPreviewRevision,
      showChangesPanel,
      toggleChangesPanel,
      showPublicationPanel,
      togglePublicationPanel,
      previewSandboxId,
      previewSandboxHints,
      selectedSandbox,
      sandboxContext,
      isPreviewActive,
      isLoadingSandboxes,
      isFetchingSandboxes,
      sandboxesError,
      refetchSandboxes,
      sandboxes,
      selectPreviewSandbox,
    ]
  );

  return <SandboxSessionContext.Provider value={value}>{children}</SandboxSessionContext.Provider>;
};

export const useSandboxSession = () => {
  const ctx = useContext(SandboxSessionContext);
  if (!ctx) {
    throw new Error('useSandboxSession must be used within SandboxSessionProvider');
  }
  return ctx;
};

export default SandboxSessionContext;
